import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronUp, Mail, Settings2, Building2, Users, Ban, History, FlaskConical } from "lucide-react";
import ConsoleShell, { Breadcrumb } from "../ConsoleShell";
import { useAuth } from "../AuthContext";
import { useConsoleMode, MODE_OPERATOR, MODE_CLIENT } from "../useConsoleMode";
import { api, errText } from "./api";
import { GmailCard, CampaignCard } from "./HeaderCards";
import { ImportCard } from "./ImportCard";
import { BatchBanner } from "./ActionBar";
import { CommandBar, KpiBlock, currentStage } from "./CommandBar";
import { PauseBanner } from "./PauseControl";
import { TestSetPanel } from "./TestSetPanel";
import { RolePanel } from "./RolePanel";
import { BlocklistPanel } from "./BlocklistPanel";
import { CompanyList } from "./CompanyList";
import { SettingsDrawer, ArchiveDialog, CampaignHistory, AuditLog } from "./Panels";
import { Tabs } from "./ui";

function SetupSection({ overview, open, onToggle, onChanged, onOpenSettings, sending }) {
  const { gmail, campaign, counts } = overview;
  return (
    <section className="bg-white border border-[#E8E1D2]" data-testid="setup-section">
      <button type="button" onClick={onToggle} className="w-full px-5 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-left hover:bg-[#FAF7F1] transition-colors" data-testid="setup-toggle" aria-expanded={open}>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5C6470]">Setup</span>
        <span className="inline-flex items-center gap-2 text-[13px] text-[#0F1825]" data-testid="setup-gmail">
          <span className={`w-2 h-2 rounded-full ${gmail.connected ? "bg-[#3F6D55]" : "bg-[#C84C21]"}`} />
          <Mail size={13} className="text-[#A09680]" /> {gmail.connected ? gmail.email : "Gmail not connected"}
        </span>
        <span className="text-[#E8E1D2]">|</span>
        <span className="text-[13px] text-[#0F1825] truncate max-w-[360px]" data-testid="setup-campaign">{campaign.name || "Untitled campaign"}<span className="text-[#A09680]"> · {counts.total} contacts</span></span>
        <span className="ml-auto inline-flex items-center gap-3">
          <button type="button" onClick={(e) => { e.stopPropagation(); onOpenSettings(); }} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470] hover:text-[#C84C21]" data-testid="open-settings-top"><Settings2 size={12} /> Signature & limits</button>
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] inline-flex items-center gap-1">{open ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> Mailbox · campaign · upload</>}</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-[#F0EBDC] p-4 md:p-5 space-y-4" data-testid="setup-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GmailCard gmail={gmail} onChanged={onChanged} batchActive={sending} />
            <CampaignCard key={campaign.id} campaign={campaign} counts={counts} onChanged={onChanged} onOpenSettings={onOpenSettings} />
          </div>
          <ImportCard onImported={onChanged} lastImport={campaign.last_import} />
        </div>
      )}
    </section>
  );
}

export default function Outreach() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("prospects");
  const [setupOpen, setSetupOpen] = useState(null); // null = decide from data once
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const qRef = useRef(q);
  qRef.current = q;
  const pageRef = useRef(page);
  pageRef.current = page;
  const listSeq = useRef(0);

  const role = (user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "bd" || !!user?.outreach_access;
  const hasModeToggle = role !== "reviewer" && role !== "bd";

  // Outreach is an Operator View surface: entering it switches to Operator View;
  // switching back to Client Workspace while here leaves the page. (Accounts without a
  // mode toggle — reviewers with a grant, BD — have nothing to force.)
  const [mode, setMode] = useConsoleMode();
  const prevMode = useRef(null);
  useEffect(() => {
    if (!isAdmin || !hasModeToggle) return;
    if (prevMode.current === null) {
      prevMode.current = mode;
      if (mode !== MODE_OPERATOR) setMode(MODE_OPERATOR);
      return;
    }
    if (prevMode.current === MODE_OPERATOR && mode === MODE_CLIENT) nav("/console/engagements", { replace: true });
    prevMode.current = mode;
  }, [mode, setMode, isAdmin, hasModeToggle, nav]);

  const loadOverview = useCallback(async () => {
    try { setOverview(await api.overview()); } catch (e) { toast.error(errText(e), { id: "outreach-poll" }); }
  }, []);
  const loadList = useCallback(async () => {
    const seq = ++listSeq.current;
    try {
      const r = await api.contacts(qRef.current, undefined, pageRef.current);
      if (seq === listSeq.current) setList(r);  // ignore stale out-of-order responses
    } catch (e) { toast.error(errText(e), { id: "outreach-poll" }); }
  }, []);
  const refreshAll = useCallback(() => { loadOverview(); loadList(); setRefreshKey((k) => k + 1); }, [loadOverview, loadList]);

  useEffect(() => {
    if (!isAdmin) { toast.error("Prospect Outreach needs an access grant — ask the admin (Manage users).", { id: "outreach-admin-only" }); nav("/console/engagements", { replace: true }); return; }
    refreshAll();
    if (params.get("gmail") === "connected") { toast.success("Gmail connected"); params.delete("gmail"); setParams(params, { replace: true }); }
    if (params.get("gmail_error")) { toast.error(`Gmail connection failed: ${params.get("gmail_error")}`); params.delete("gmail_error"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Setup opens by itself only while something is missing (no mailbox or no contacts yet).
  useEffect(() => {
    if (overview && setupOpen === null) setSetupOpen(!overview.gmail.connected || overview.counts.total === 0);
  }, [overview, setupOpen]);

  // search + paging are server-side — debounce; a new search always starts at page 1
  useEffect(() => { setPage(1); }, [q]);
  useEffect(() => {
    const t = setTimeout(loadList, 250);
    return () => clearTimeout(t);
  }, [q, page, loadList]);

  // Live refresh while a research batch or a send batch is running.
  const researching = overview?.research_job?.status === "running";
  const sending = !!overview?.batch?.active;
  useEffect(() => {
    if (!researching && !sending) return undefined;
    const t = setInterval(() => { loadOverview(); loadList(); }, 8000);
    return () => clearInterval(t);
  }, [researching, sending, loadList, loadOverview]);
  const wasActive = useRef(false);
  useEffect(() => {
    const active = researching || sending;
    if (wasActive.current && !active) { loadList(); loadOverview(); }
    wasActive.current = active;
  }, [researching, sending, loadList, loadOverview]);

  // bump refreshKey too: delete-company / bulk DNC / row DNC change the Blocklist panel
  const onContactChanged = useCallback(() => { loadList(); loadOverview(); setRefreshKey((k) => k + 1); }, [loadList, loadOverview]);

  const scrollToList = () => window.scrollTo({ top: (document.querySelector('[data-testid="workspace-tabs"]')?.offsetTop || 0) - 16, behavior: "smooth" });
  const openUpload = () => {
    setSetupOpen(true);
    setTimeout(() => {
      document.querySelector('[data-testid="import-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      document.querySelector('[data-testid="import-file-input"]')?.click();
    }, 150);
  };

  if (!isAdmin) return null;

  const hasTestSet = overview?.research_job?.scope === "sample";
  const tabs = overview ? [
    { key: "prospects", label: "Prospects", icon: Building2, count: overview.counts.total },
    { key: "roles", label: "By function", icon: Users },
    { key: "blocklist", label: "Blocklist", icon: Ban, count: overview.counts.suppressed || undefined },
    { key: "history", label: "History & log", icon: History },
  ] : [];
  const stage = overview ? currentStage({ counts: overview.counts, batch: overview.batch, job: overview.research_job }) : 1;
  const crumbs = role === "bd" ? [{ label: "Prospect Outreach" }] : [{ label: "Engagements", to: "/console/engagements" }, { label: "Prospect Outreach" }];

  return (
    <ConsoleShell hideIllustrativeRibbon breadcrumb={<Breadcrumb items={crumbs} />}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-4" data-testid="page-outreach">
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21]">Business development</div>
            <h1 className="font-serif text-3xl md:text-4xl text-[#0F1825] mt-2 leading-[1.1] tracking-tight">Prospect Outreach.</h1>
          </div>
          <p className="text-[#5C6470] text-[13px] leading-relaxed max-w-2xl md:pb-1" data-testid="outreach-lede">
            {stage === 1 && "Connect your mailbox and upload a prospect list — then every company gets live asset research and every person a role-aware draft."}
            {stage === 2 && "Research + draft writes one platform-first email per person (listening → PentaVoice → triangulation → insight). Drafts appear as each company completes."}
            {stage === 3 && "Read a few drafts, then approve. Nothing is ever sent without your explicit approval."}
            {stage === 4 && "Approved emails leave one at a time, 20–40 s apart, from your own Gmail — on the server, even with your laptop closed. Replies and bounces sync every 30 minutes."}
          </p>
        </div>

        {!overview ? (
          <div className="flex items-center gap-2 text-[#5C6470] text-[13px]"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="space-y-2" data-testid="live-banners">
              <PauseBanner pause={overview.pause} onChanged={refreshAll} />
              <BatchBanner batch={overview.batch} onChanged={refreshAll} />
            </div>
            <SetupSection overview={overview} open={!!setupOpen} onToggle={() => setSetupOpen((o) => !o)} onChanged={refreshAll} onOpenSettings={() => setSettingsOpen(true)} sending={sending} />
            <CommandBar overview={overview} onChanged={refreshAll} onArchive={() => setArchiveOpen(true)} onUpload={openUpload} onOpenSettings={() => setSettingsOpen(true)} />
            <KpiBlock overview={overview} />

            <div className="pt-2">
              <Tabs tabs={[...tabs, ...(hasTestSet ? [{ key: "tests", label: "Test set", icon: FlaskConical, count: (overview.research_job.picks || []).length }] : [])]} active={tab} onChange={setTab} testid="workspace-tabs" />
              <div className="pt-4 space-y-4" data-testid={`tab-panel-${tab}`}>
                {tab === "prospects" && (
                  <CompanyList data={list} q={q} onQ={setQ} onChanged={onContactChanged} counts={overview.counts} onPage={(pg) => { setPage(pg); scrollToList(); }} />
                )}
                {tab === "roles" && <RolePanel refreshKey={refreshKey} onChanged={refreshAll} />}
                {tab === "blocklist" && <BlocklistPanel refreshKey={refreshKey} onChanged={refreshAll} />}
                {tab === "history" && (<><CampaignHistory refreshKey={refreshKey} /><AuditLog refreshKey={refreshKey} /></>)}
                {tab === "tests" && hasTestSet && <TestSetPanel job={overview.research_job} onFocus={(email) => { setQ(email); setTab("prospects"); scrollToList(); }} />}
              </div>
            </div>

            <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={overview.settings} onSaved={() => loadOverview()} />
            <ArchiveDialog open={archiveOpen} onClose={() => setArchiveOpen(false)} defaultName={overview.campaign.name} onArchived={() => { setQ(""); refreshAll(); }} />
          </>
        )}
      </div>
    </ConsoleShell>
  );
}
